/**
 * ContactSummary component - displays formal contact summary.
 */

import { useState, useEffect, useCallback } from "react";
import { getContactSummary } from "../../lib/api";
import type { ContactSummary as ContactSummaryType } from "../../lib/types";

// =============================================================================
// Styles
// =============================================================================

const styles: Record<string, React.CSSProperties> = {
  container: {
    flex: 1,
    overflow: "auto",
    padding: "20px",
    background: "#ffffff",
  },
  loading: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "40px",
    color: "#6b7280",
    fontSize: "14px",
  },
  error: {
    padding: "20px",
    textAlign: "center",
    color: "#ef4444",
    fontSize: "14px",
  },
  empty: {
    padding: "40px 20px",
    textAlign: "center",
    color: "#9ca3af",
    fontSize: "14px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "20px",
  },
  title: {
    fontSize: "16px",
    fontWeight: 600,
    color: "#111827",
    margin: 0,
  },
  regenerateButton: {
    padding: "6px 12px",
    background: "#f3f4f6",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    fontSize: "12px",
    color: "#374151",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  section: {
    marginBottom: "20px",
  },
  sectionLabel: {
    fontSize: "11px",
    fontWeight: 600,
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: "6px",
  },
  sectionContent: {
    fontSize: "14px",
    color: "#374151",
    lineHeight: 1.6,
  },
  list: {
    margin: 0,
    paddingLeft: "20px",
  },
  listItem: {
    fontSize: "14px",
    color: "#374151",
    lineHeight: 1.6,
    marginBottom: "4px",
  },
  summaryBox: {
    padding: "16px",
    background: "#f0f9ff",
    border: "1px solid #bae6fd",
    borderRadius: "8px",
    marginBottom: "20px",
  },
  summaryText: {
    fontSize: "14px",
    color: "#0c4a6e",
    lineHeight: 1.6,
    margin: 0,
  },
  resolutionBox: {
    padding: "16px",
    background: "#f0fdf4",
    border: "1px solid #bbf7d0",
    borderRadius: "8px",
    marginTop: "20px",
  },
  resolutionLabel: {
    fontSize: "11px",
    fontWeight: 600,
    color: "#166534",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: "6px",
  },
  resolutionText: {
    fontSize: "14px",
    color: "#166534",
    lineHeight: 1.6,
    margin: 0,
  },
  divider: {
    height: "1px",
    background: "#e5e7eb",
    margin: "20px 0",
  },
  copyButton: {
    padding: "8px 16px",
    background: "#3b82f6",
    border: "none",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: 500,
    color: "white",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginTop: "20px",
  },
  copyButtonSuccess: {
    background: "#10b981",
  },
};

// =============================================================================
// Icons
// =============================================================================

function RefreshIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  );
}

function CopyIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

function CheckIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

// =============================================================================
// Helper Components
// =============================================================================

interface SectionProps {
  label: string;
  children: React.ReactNode;
}

function Section({ label, children }: SectionProps) {
  if (!children) return null;
  return (
    <div style={styles.section}>
      <div style={styles.sectionLabel}>{label}</div>
      {children}
    </div>
  );
}

interface BulletListProps {
  items?: string[];
}

function BulletList({ items }: BulletListProps) {
  if (!items || items.length === 0 || (items.length === 1 && items[0].toLowerCase() === "n/a")) {
    return <div style={styles.sectionContent}>None</div>;
  }
  return (
    <ul style={styles.list}>
      {items.map((item, index) => (
        <li key={index} style={styles.listItem}>
          {item}
        </li>
      ))}
    </ul>
  );
}

// =============================================================================
// Main Component
// =============================================================================

interface ContactSummaryProps {
  contactId: string;
}

export function ContactSummary({ contactId }: ContactSummaryProps) {
  const [summaryData, setSummaryData] = useState<ContactSummaryType | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const fetchSummary = useCallback(
    async (regenerate = false) => {
      if (!contactId) return;

      setIsLoading(true);
      setError(null);

      try {
        const data = await getContactSummary(contactId, regenerate);

        if (data.error) {
          setError(data.error);
        } else if (data.summary) {
          setSummaryData(data.summary);
        } else {
          setError("No summary available");
        }
      } catch (err) {
        setError("Failed to load summary");
        console.error("Error fetching summary:", err);
      } finally {
        setIsLoading(false);
      }
    },
    [contactId]
  );

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const handleRegenerate = () => {
    fetchSummary(true);
  };

  const handleCopy = async () => {
    if (!summaryData) return;

    const text = formatSummaryAsMarkdown(summaryData, contactId);

    // Try modern clipboard API first, fall back to execCommand for iframe compatibility
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for iframes where clipboard API is restricted
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback if clipboard API fails (e.g., in iframe)
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (isLoading && !summaryData) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Generating contact summary...</div>
      </div>
    );
  }

  if (error && !summaryData) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>{error}</div>
      </div>
    );
  }

  if (!summaryData) {
    return (
      <div style={styles.container}>
        <div style={styles.empty}>No summary available yet.</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Contact Summary</h2>
        <button style={styles.regenerateButton} onClick={handleRegenerate} disabled={isLoading}>
          <RefreshIcon size={12} />
          {isLoading ? "Regenerating..." : "Regenerate"}
        </button>
      </div>

      {/* Summary */}
      <div style={styles.summaryBox}>
        <p style={styles.summaryText}>{summaryData.summary}</p>
      </div>

      {/* Customer Issue */}
      <Section label="Customer Issue">
        <div style={styles.sectionContent}>{summaryData.customer_issue}</div>
      </Section>

      {/* Key Points */}
      <Section label="Key Points">
        <BulletList items={summaryData.key_points} />
      </Section>

      <div style={styles.divider} />

      {/* Actions Taken */}
      <Section label="Actions Taken">
        <BulletList items={summaryData.actions_taken} />
      </Section>

      {/* Follow-ups */}
      <Section label="Follow-ups Required">
        <BulletList items={summaryData.follow_ups} />
      </Section>

      {/* Resolution */}
      <div style={styles.resolutionBox}>
        <div style={styles.resolutionLabel}>Resolution</div>
        <p style={styles.resolutionText}>{summaryData.resolution}</p>
      </div>

      {/* Copy Button */}
      <button
        style={{
          ...styles.copyButton,
          ...(copied ? styles.copyButtonSuccess : {}),
        }}
        onClick={handleCopy}
      >
        {copied ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
        {copied ? "Copied!" : "Copy to Clipboard"}
      </button>
    </div>
  );
}

/**
 * Format summary as markdown for copying.
 */
function formatSummaryAsMarkdown(summaryData: ContactSummaryType, contactId: string): string {
  const lines = [
    "# Contact Summary",
    "",
    `**Contact ID:** ${contactId}`,
    "",
    summaryData.summary,
    "",
    "## Customer Issue",
    "",
    summaryData.customer_issue,
    "",
    "## Key Points",
    "",
    ...(summaryData.key_points || []).map((p) => `- ${p}`),
    "",
    "## Actions Taken",
    "",
    ...(summaryData.actions_taken || []).map((a) => `- ${a}`),
    "",
    "## Follow-ups Required",
    "",
    ...(summaryData.follow_ups || []).map((f) => `- ${f}`),
    "",
    "## Resolution",
    "",
    summaryData.resolution,
  ];
  return lines.join("\n");
}

export default ContactSummary;
